import { useCallback, useState } from 'react';
import {
  InfiniteData,
  MutationFunction,
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from 'react-query';
import startCase from 'lodash/startCase';
import { Colors } from '@app/theme';
import {
  queryCreate,
  queryDelete,
  queryGet,
  queryReplace,
  queryUpdate,
} from './fetch';
import { Organization } from './organization';
import { PaginatedResults } from './participationCache';
import { Participation } from './participation';
import { useCurrentOrganizationId } from './currentOrganization';
import {
  useGetSessionAnalyticsParams,
  useSessionAnalyticsParams,
} from './utils/analyticsUtils';
import {
  useTrack,
  useTrackSessionCreated,
  useTrackSessionStep,
} from '@app/analytics';
import { SessionLink } from './sessionLink';
import { NewQuota } from './quota';
import { GroupedCountResult } from './participationFieldAnswers';

export enum StudySubject {
  B2B = 'B2B',
  B2C = 'B2C',
}

export enum StudyType {
  MODERATED = 'MODERATED',
  UNMODERATED = 'UNMODERATED',
  DIARY = 'DIARY',
}

export enum SessionStatus {
  DRAFT = 'DRAFT',
  LIVE = 'LIVE',
  FULL = 'FULL',
  PAUSED = 'PAUSED',
  CLOSED = 'CLOSED',
}

export enum SessionFormat {
  PHYSICAL_MEETING = 'PHYSICAL_MEETING',
  VIDEO_CALL = 'VIDEO_CALL',
  PHONE_CALL = 'PHONE_CALL',
  IN_PARTICIPANT_PLACE = 'IN_PARTICIPANT_PLACE',
  SURVEY = 'SURVEY',
  RECORDING = 'RECORDING',
}

export enum OwnRecruitmentOptions {
  NO_INCENTIVE = 'NO_INCENTIVE',
  I_PAY = 'I_PAY',
  TANDEMZ_PAY = 'TANDEMZ_PAY',
}

export enum TabStatus {
  outOfQuota = 'outOfQuota',
  applied = 'applied',
  selected = 'selected',
  approved = 'approved',
  participated = 'participated',
  gaveUp = 'gaveUp',
  rejected = 'rejected',
  other_moderated = 'other_moderated',
  other_unmoderated = 'other_unmoderated',
}

export interface Appointment {
  id: string;
  sessionId: string;
  session?: Session;
  date: string;
}

export interface ParticipationTag {
  id: string;
  name: string;
  color: Colors;
  sessionId: string;
}

export enum RequiredMediaSource {
  VIDEO = 'VIDEO',
  AUDIO = 'AUDIO',
  SCREEN_SHARING = 'SCREEN_SHARING',
}

export enum DevicesCategory {
  COMPUTER = 'COMPUTER',
  TABLET = 'TABLET',
  SMARTPHONE = 'SMARTPHONE',
}

export enum ParticipationReportReason {
  NO_SHOW = 'no_show',
  PROFILE_LIE = 'profile_lie',
  CONNECTION_ISSUES = 'connection_issues',
  TECHNICAL_REQUIREMENTS_ISSUES = 'technical_requirements_issues',
  BAD_SURROUNDINGS = 'bad_surroundings',
  EMERGENCY_WITH_NOTICE = 'emergency_with_notice',
  NOT_SERIOUS_ANSWERS = 'not_serious_answers',
}

export enum ParticipationRating {
  NOT_GOOD = 'NOT_GOOD',
  OK = 'OK',
  VERY_GOOD = 'VERY_GOOD',
}

export enum ActionMadeByRole {
  ADMIN = 'ADMIN',
  CLIENT = 'CLIENT',
  PARTICIPANT = 'PARTICIPANT',
  AUTOMATIC = 'AUTOMATIC',
}

export enum SessionQuota {
  ALLOWED_DEVICES_CATEGORY = 'ALLOWED_DEVICES_CATEGORY',
}

export interface Session {
  id: string;

  organization: Organization;
  organizationId: string;

  recruitmentId?: string;
  planId?: string;
  quoteId?: string;

  format?: SessionFormat;
  studyType: StudyType;
  studySubject: StudySubject;
  title?: string;
  internalName?: string;
  duration: number;
  durationDisplayed?: string;
  description?: string;
  incentiveText?: string;
  showHowItWorks: boolean;
  allowJoinPanel: boolean;
  locale: string;
  availableTags?: ParticipationTag[];
  requiredMediaSource?: RequiredMediaSource[];
  allowedDevicesCategories?: DevicesCategory[];
  availableDevicesCategories?: DevicesCategory[];
  allowedDevicesCategoriesQuotas?: NewQuota[];
  links?: SessionLink[];

  startDate?: string;
  endDate?: string;

  studyLink?: string;

  phoneVisibilityConsent?: boolean;

  numberOfBooked: number;
  numberOfParticipantRequired: number;
  appointmentBuffer?: number;

  published: boolean;
  participations?: Participation[];
  autoRejectDisabled?: boolean;

  hiddenColumns: Record<TabStatus, string[]>;

  validFormSteps?: string[];
  archivedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  publishedAt?: string;
  cancelledAt?: string;
  version: number;
  status?: SessionStatus;
  ownerId: string;
  emergency?: number;
  taskSettingId?: string;

  dismissInformationReviewCard?: boolean;
  dismissInformationAcceptedCard?: boolean;
  dismissInformationBookedCard?: boolean;
  dismissInformationRatingCard?: boolean;

  assignedToId?: string;

  unmoderatedOverflowAcknoledgedAt?: Date;
  billingNote?: string;
}

export interface PublicSession extends Session {
  address?: string;
}

export interface StudyCreationDto {
  organizationId: string;
  quoteId?: string;
  studyType: StudyType;
  studySubject: StudySubject;
  format?: SessionFormat;
  duration: number;
  durationDisplayed?: string;
  numberOfParticipantRequired: number;
  offerIds: string[];
  priceThierId?: string;
  ownRecruitmentOption?: OwnRecruitmentOptions;
}

// #FIXME: remove study link from here
export interface StudyEditionDto {
  internalName?: string;
  numberOfParticipantRequired?: number;
  studyLink?: string;

  title?: string;
  description?: string;
  duration?: number;
  incentiveText?: string;
  referralIncentiveId?: string | null;
  referralActivated?: boolean;
  referralText?: string | null;

  appointmentBuffer?: number;
  billingNote?: string;
  hiddenColumns?: Record<TabStatus, string[]>;
}

export const useCreateStudy = () => {
  const track = useTrackSessionCreated();
  const getSessionParams = useGetSessionAnalyticsParams();

  return useMutation<Session, undefined, Partial<StudyCreationDto>>(
    queryCreate('/studies'),
    {
      onSuccess: async (data: Session) => {
        const params = await getSessionParams(data);
        track(params);
      },
    },
  );
};

export const useUpdateStudy = (
  id?: string,
  options: { noInvalidate?: boolean } = {},
) => {
  const queryClient = useQueryClient();
  const onSuccess = useCallback(
    (session: Session) => {
      if (options.noInvalidate) {
        queryClient.setQueryData(['sessions', id], session);
        return;
      }

      queryClient.invalidateQueries(['sessions', id]);
      queryClient.invalidateQueries(['sessions', id, 'quotas']);
    },
    [queryClient, id, options.noInvalidate],
  );
  return useMutation<Session, undefined, StudyEditionDto>(
    queryUpdate(`/studies/${id}`),
    { onSuccess },
  );
};

export const useUpdateStudyAppointments = (id?: string) => {
  const queryClient = useQueryClient();
  const onSuccess = useCallback(
    (appointments: Appointment[]) => {
      queryClient.setQueryData(['studies', id, 'appointments'], appointments);
      queryClient.invalidateQueries([
        'studies',
        id,
        'appointments',
        'availables',
      ]);
      queryClient.invalidateQueries([
        'studies',
        id,
        'appointments',
        'not-booked',
      ]);
    },
    [queryClient, id],
  );
  return useMutation<Appointment[], undefined, string[]>(
    queryReplace(`/studies/${id}/appointments`),
    { onSuccess },
  );
};

export const useMarkStudyStepAsValidated = (id?: string) => {
  const queryClient = useQueryClient();
  const track = useTrackSessionStep();
  const params = useSessionAnalyticsParams(id);
  const onSuccess = useCallback(
    async (session: Session, { step }: { step: string }) => {
      track(startCase(step), params);

      queryClient.invalidateQueries(['sessions', id]);
    },
    [queryClient, id, track, params],
  );
  return useMutation<Session, undefined, { step: string }>(
    queryCreate(`/studies/${id}/validFormSteps`),
    { onSuccess },
  );
};

export const useSession = (id?: string) => {
  return useQuery<Session>(['sessions', id], queryGet(`/sessions/${id}`), {
    enabled: !!id,
  });
};

export const useSessionProposals = () => {
  return useQuery<PublicSession[]>(
    ['public_sessions'],
    queryGet(`/public-sessions`),
    {
      retry: false,
    },
  );
};

export const usePublicSession = (id?: string) => {
  return useQuery<PublicSession>(
    ['public_sessions', id],
    queryGet(`/public-sessions/${id}`),
    {
      enabled: !!id,
    },
  );
};

export const useShortOrganizationSessions = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useQuery<Session[]>(
    ['organizations', orgId, 'sessions', 'short'],
    queryGet(`/organizations/${orgId}/sessions/short`),
    {
      enabled: !!orgId,
      onSuccess: (sessions: Session[]) => {
        sessions.forEach((session) => {
          queryClient.setQueryData(['sessons', session.id, 'short'], session);
        });
      },
    },
  );
};

export const useOrganizationSessions = (
  orgId?: string,
  archiveFilter?: string,
) => {
  const queryClient = useQueryClient();
  const onSuccess = useCallback(
    (res: InfiniteData<PaginatedResults<Session>>) => {
      res.pages.forEach(({ data }) => {
        data.forEach((session) => {
          queryClient.setQueryData(['sessions', session.id], session);
        });
      });
    },
    [queryClient],
  );

  return useInfiniteQuery<PaginatedResults<Session>>(
    ['organizations', orgId, 'sessions', archiveFilter],
    ({ pageParam }) =>
      queryGet(`/organizations/${orgId}/sessions`, {
        cursor: pageParam,
        archived: archiveFilter,
      })(),
    {
      enabled: !!orgId,
      getNextPageParam: (lastPage, allPages) => {
        const downloadedCount = allPages.reduce((acc, page) => {
          return acc + page.data.length;
        }, 0);
        if (downloadedCount >= lastPage.pagination.count) {
          return undefined;
        }
        return lastPage.pagination.cursor;
      },
      onSuccess,
    },
  );
};

export const useLiveSessions = (orgId?: string) => {
  return useQuery<Session[]>(
    ['organizations', orgId, 'sessions', 'live'],
    queryGet(`/organizations/${orgId}/sessions/live`),
    {
      enabled: !!orgId,
    },
  );
};

const useMutateSession = (
  query: MutationFunction<Session, void>,
  id: string | undefined,
  options: UseMutationOptions<Session, Error, any | void> = {},
  trackEvent?: string,
) => {
  const queryClient = useQueryClient();
  const track = useTrack();
  const params = useSessionAnalyticsParams(id);

  const onSuccess = useCallback(
    async (session: Session, variables: void, ctx: any) => {
      queryClient.setQueryData(['sessions', id], session);

      if (trackEvent) {
        track(trackEvent, params);
      }

      if (options.onSuccess) {
        return options.onSuccess(session, variables, ctx);
      }
      return;
    },
    [queryClient, id, options, params], //eslint-disable-line react-hooks/exhaustive-deps
  );
  return useMutation<Session, Error, void>(() => query(), {
    ...options,
    onSuccess,
  });
};

export const usePublishSession = (
  id: string | undefined,
  options: UseMutationOptions<Session, Error, any> = {},
) => {
  return useMutateSession(
    queryUpdate(`/studies/${id}/publish`),
    id,
    options,
    'Published Session',
  );
};

export const useDeleteSession = (
  id: string | undefined,
  options: UseMutationOptions<Session, Error, any> = {},
) => {
  return useMutateSession(
    queryDelete(`/sessions/${id}`),
    id,
    options,
    'Deleted Session',
  );
};

export const useArchiveSession = (
  id: string | undefined,
  options: UseMutationOptions<Session, Error, any> = {},
) => {
  return useMutateSession(
    queryUpdate(`/sessions/${id}/archive`),
    id,
    options,
    'Archived Session',
  );
};

export const useRestoreSession = (
  id: string | undefined,
  options: UseMutationOptions<Session, Error, any> = {},
) => {
  return useMutateSession(
    queryUpdate(`/sessions/${id}/restore`),
    id,
    options,
    'Restored Session',
  );
};

export const usePauseSession = (
  id: string | undefined,
  options: UseMutationOptions<Session, Error, any> = {},
) => {
  return useMutateSession(
    queryUpdate(`/sessions/${id}/pause`),
    id,
    options,
    'Paused Session',
  );
};

export const useResumeSession = (
  id: string | undefined,
  options: UseMutationOptions<Session, Error, any> = {},
) => {
  return useMutateSession(
    queryUpdate(`/sessions/${id}/resume`),
    id,
    options,
    'Resumed Session',
  );
};

export const useCloseSession = (
  id: string | undefined,
  options: UseMutationOptions<Session, Error, any> = {},
) => {
  return useMutateSession(
    queryUpdate(`/sessions/${id}/close`),
    id,
    options,
    'Closed Session',
  );
};

export const useCleanSession = (
  id: string | undefined,
  options: UseMutationOptions<Session, Error, any> = {},
) => {
  return useMutateSession(
    queryUpdate(`/sessions/${id}/clean`),
    id,
    options,
    'Cleaned Session',
  );
};

export const useAddAvailableTag = (
  id: string | undefined,
  options: UseMutationOptions<
    ParticipationTag,
    Error,
    { name: string; color: string }
  > = {},
) => {
  const queryClient = useQueryClient();
  const onSuccess = useCallback(
    (tag: ParticipationTag, variables: any, ctx: any) => {
      queryClient.setQueryData(['sessions', id], (session?: Session) => {
        if (!session) {
          throw Error('No cache');
        }
        return {
          ...session,
          availableTags: [...(session.availableTags || []), tag],
        };
      });
      if (options.onSuccess) {
        return options.onSuccess(tag, variables, ctx);
      }
    },
    [queryClient, id, options],
  );
  return useMutation<ParticipationTag, Error, { name: string; color: string }>(
    queryCreate(`/sessions/${id}/available-tags`),
    {
      ...options,
      onSuccess,
    },
  );
};

export const useAddNote = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, { message: string }>(
    queryCreate(`/sessions/${id}/notes`),
    {
      onSuccess: () => {
        setTimeout(() => {
          queryClient.invalidateQueries(['sessions', id, 'history']);
        }, 500);
      },
    },
  );
};

export const useToggleAutomaticRejectDisabled = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, void>(
    queryUpdate(`/sessions/${id}/toggle-auto-reject-disabled`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sessions', id]);
      },
    },
  );
};

export const useDismissInformationCards = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    Session,
    Error,
    {
      dismissInformationReviewCard?: boolean;
      dismissInformationAcceptedCard?: boolean;
      dismissInformationBookedCard?: boolean;
      dismissInformationRatingCard?: boolean;
    }
  >((dto) => queryUpdate(`/sessions/${id}/dismiss-information-cards`)(dto), {
    onSuccess: () => {
      queryClient.invalidateQueries(['sessions', id]);
    },
  });
};

export const useShortSession = (id?: string) => {
  const organizationId = useCurrentOrganizationId();
  const { isLoading } = useShortOrganizationSessions(organizationId);

  return useQuery<Session>(
    ['sessons', id, 'short'],
    queryGet(`/sessions/${id}/short`),
    {
      enabled: !!id && !isLoading,
      staleTime: 60 * 1000,
    },
  );
};

export const useAssignSessionToMe = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<Session, Error, void>(
    queryCreate(`/sessions/${id}/assignedTo/me`),
    {
      onSuccess: (session: Session) => {
        queryClient.setQueryData(['sessions', id], session);
      },
    },
  );
};

export const useGroupedParticipationsItemsCount = (sessionId?: string) => {
  return useQuery<GroupedCountResult>(
    ['sessions', sessionId, 'participations', 'items-count'],
    queryGet(`/sessions/${sessionId}/participations/items-count`),
    {
      enabled: !!sessionId,
    },
  );
};

export enum IaPromptName {
  SCREENER = 'screener',
  TITLE_AND_DESCRIPTION = 'title-and-description',
  TARGET = 'target',
}

export interface IaTitleAndDescriptionProposal {
  title: string;
  description: string;
}

export interface IaSuggestion<Result = unknown> {
  id: string;
  sessionId: string;
  promptId: string;
  promptName: IaPromptName | string;
  generationInProgress: boolean;
  generatedAt?: string | null;
  result?: Result | null;
  createdAt: string;
  updatedAt: string;
}

export const filterIaSuggestions = <Result>(
  suggestions: IaSuggestion[] | undefined,
  promptName: IaPromptName,
) =>
  (suggestions || [])
    .filter((suggestion) => suggestion.promptName === promptName)
    .reverse() as IaSuggestion<Result>[];

const IA_SUGGESTION_POLLING_INTERVAL = 5000;

export const useSessionIaSuggestion = (sessionId?: string) => {
  const [generationInProgress, setGenerationInProgress] = useState(false);

  return useQuery<IaSuggestion[]>(
    ['sessions', sessionId, 'ia-suggestions'],
    queryGet(`/sessions/${sessionId}/ia-suggestions`),
    {
      enabled: !!sessionId,
      refetchInterval: generationInProgress
        ? IA_SUGGESTION_POLLING_INTERVAL
        : false,
      onSuccess: (suggestions) =>
        setGenerationInProgress(
          suggestions.some((suggestion) => suggestion.generationInProgress),
        ),
    },
  );
};

export const useRequestSessionIaSuggestion = (sessionId?: string) => {
  const queryClient = useQueryClient();

  return useMutation<IaSuggestion, Error, IaPromptName>(
    (promptName) =>
      queryCreate(`/sessions/${sessionId}/ia-suggestions`)({ promptName }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'sessions',
          sessionId,
          'ia-suggestions',
        ]);
      },
    },
  );
};

export type IaScreenerChoiceStatus = 'OK' | 'KO';

export interface IaScreenerChoice {
  label: string;
  status: IaScreenerChoiceStatus;
  /** "END" when the choice must end the screener */
  jump?: string;
}

export interface IaScreenerQuestion {
  id: string;
  type: 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | string;
  title: string;
  purpose?: string;
  choices: IaScreenerChoice[];
}

export type IaScreenerSuggestion = IaSuggestion<{
  questions: IaScreenerQuestion[];
}>;

export interface IaTargetAge {
  min?: number | null;
  max?: number | null;
  reason?: string;
}

export interface IaTargetValues {
  values?: string[];
  reason?: string;
}

export interface IaTargetResult {
  age?: IaTargetAge;
  gender?: IaTargetValues;
  country?: IaTargetValues;
  language?: IaTargetValues;
  familyStatus?: IaTargetValues;
  professionalSituation?: IaTargetValues;
  educationLevel?: IaTargetValues;
}

export type IaTargetSuggestion = IaSuggestion<IaTargetResult>;
